import { FC, useContext, createContext, ReactNode, useMemo } from 'react';
import { ThemeProvider as DSThemeProvider } from '@robocorp/theme';
import { InterfaceTheme } from '@robocorp/robocloud-internal-cds-sdk/lib/src/types/RcInstanceDB';
import { useSystemTheme, ViewportProvider } from '@robocorp/components';
import { LocalStorageItemKeys, useLocalStorage } from './useStorage';

type ThemeContextType = {
  /**
   * The theme in effect, falling back to `system` when no preference is stored.
   */
  theme: InterfaceTheme;

  /**
   * The preference held by this browser, or `undefined` when it holds none. Distinguishing
   * the two matters when deciding whether a profile without a theme may overwrite it.
   */
  storedTheme: InterfaceTheme | undefined;

  setTheme: (theme: InterfaceTheme) => void;
};

const ThemeContext = createContext<ThemeContextType>({
  theme: InterfaceTheme.system,
  storedTheme: undefined,
  setTheme: () => null,
});

const useTheme = () => {
  const context = useContext(ThemeContext);
  return context;
};

export const ThemeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const systemTheme = useSystemTheme();

  const { storageValue: storedTheme, setStorageValue: setTheme } = useLocalStorage<
    InterfaceTheme | undefined
  >({
    key: LocalStorageItemKeys.THEME,
    initialValue: undefined,
    sync: true,
  });

  const theme = storedTheme ?? InterfaceTheme.system;

  const context = useMemo(() => ({ theme, storedTheme, setTheme }), [theme, storedTheme, setTheme]);

  return (
    <ThemeContext.Provider value={context}>
      <DSThemeProvider name={theme === InterfaceTheme.system ? systemTheme : theme}>
        <ViewportProvider>{children}</ViewportProvider>
      </DSThemeProvider>
    </ThemeContext.Provider>
  );
};

export default useTheme;
